// Retry journal is tab-local and scoped to the authenticated user, never to a
// global anonymous browser. It contains only the submitted business payload
// (no token/cookie). Do not expire an uncertain operation or generate a new key
// for it. Closing a dialog or reloading the tab must not silently duplicate it.
import { getCurrentSessionSnapshot } from "./sessionIdentity";
export const STOCK_POSTING_JOURNAL_EVENT = "hb:stock-posting-journal";
const PREFIX = "hb-stock-posting-v1:";
const flights = new Map();
function error(code, message) { return Object.assign(new Error(message), { code, status: 409 }); }
function storage() {
  try { return window.sessionStorage; } catch (_) { throw error("STOCK_RETRY_STORAGE_UNAVAILABLE", "Retry journal is unavailable; no stock request was sent."); }
}
function owner() {
  const id = getCurrentSessionSnapshot()?.userId;
  if (!id) throw error("STOCK_RETRY_SESSION_REQUIRED", "A signed-in tab is required for stock posting.");
  return String(id);
}
function scopeFor(url) {
  if (/^\/stock\/controls\/(close|reopen|reconcile)$/.test(url)) return "stock_controls";
  if (/^\/visits\/\d+(?:\/calendar)?$/.test(url)) return "visits";
  if (/^\/visits\/\d+\/(?:check|uncheck)$/.test(url)) return "visits";
  if (/^\/sales\/documents\/\d+\/post$/.test(url)) return "sales";
  if (/^\/stock\/(receive|issue|transfer|correction)$/.test(url)) return "stock";
  if (/^\/stock\/movements\/\d+\/correction$/.test(url)) return "stock";
  if (url === "/sanitary/solutions") return "solution";
  if (/^\/service-consumption\/material-consumptions\/\d+\/replace-product$/.test(url)) return "material";
  throw error("STOCK_RETRY_SCOPE_INVALID", "Unsupported stock-posting URL.");
}
function identity(scope) { return `${PREFIX}${owner()}:${scope}`; }
function canonical(v) {
  if (v && !Array.isArray(v) && typeof v === "object") return Object.fromEntries(Object.keys(v).sort().filter(k => v[k] !== undefined).map(k => [k, canonical(v[k])]));
  if (Array.isArray(v)) return v.map(canonical);
  return v;
}
function announce() { window.dispatchEvent(new Event(STOCK_POSTING_JOURNAL_EVENT)); }
function write(key, record) {
  try { storage().setItem(key, JSON.stringify(record)); } catch (_) { throw error("STOCK_RETRY_STORAGE_UNAVAILABLE", "Retry journal could not be saved; no stock request was sent."); }
  announce();
}
function remove(key) { try { storage().removeItem(key); } catch (_) { /* retaining the same key is safe */ } announce(); }
function read(key) {
  const text=storage().getItem(key);
  if(!text)return null;
  try {
    const r=JSON.parse(text);
    if(!["post","put","patch"].includes(r.method || "post")||r.owner!==owner()||!r.key||!r.url||!r.payload||scopeFor(r.url)!==r.scope)throw new Error("Invalid retry journal");
    return r;
  } catch (_) { throw error("STOCK_RETRY_JOURNAL_INVALID", "Stored operation requires verification. Do not create a new request until it is checked."); }
}
function uuid() {
  if (globalThis.crypto?.randomUUID) return globalThis.crypto.randomUUID();
  if (!globalThis.crypto?.getRandomValues) throw error("STOCK_RETRY_STORAGE_UNAVAILABLE", "Secure operation-key generation is unavailable; no request was sent.");
  return Array.from(globalThis.crypto.getRandomValues(new Uint8Array(24)), b => b.toString(16).padStart(2,"0")).join("");
}
function definitelyRejected(err) {
  const status=err?.response?.status, body=err?.response?.data;
  return status>=400 && status<500 && ![401,403,408,429].includes(status) && body?.code && !body.outcome_unknown &&
    !["STOCK_OPERATION_KEY_CONFLICT","STOCK_OPERATION_INCOMPLETE","STOCK_POSTING_OUTCOME_UNKNOWN"].includes(body.code);
}
async function send(client, key, record, config) {
  if(flights.has(key))return flights.get(key);
  const request=Promise.resolve().then(async()=>{
    try {
      const res=await client[record.method || "post"](record.url, record.payload, { ...config, headers: { ...config?.headers, "Idempotency-Key":record.key } });
      remove(key);
      return res;
    } catch(err) {
      if(definitelyRejected(err))remove(key);
      else {
        err.outcomeUnknown=true;
        // Response not received, 5xx, authorization loss, or key conflict: keep
        // the exact request. Explicit recovery below resends *the same* key.
        announce();
      }
      throw err;
    } finally { flights.delete(key); }
  });
  flights.set(key,request);return request;
}
async function idempotentWrite(client, method, url, payload, config = {}) {
  const scope=scopeFor(url), key=identity(scope);
  const body=JSON.parse(JSON.stringify(payload));
  const fingerprint=JSON.stringify({url,payload:canonical(body), ...(method === "post" ? {} : {method})});
  let record=read(key);
  if(record && record.fingerprint!==fingerprint)throw error("STOCK_OPERATION_PENDING_REVIEW", "A previous stock request is unconfirmed. Use 'Retry the same request' before posting different data.");
  if(!record){record={owner:owner(),scope,url,...(method === "post" ? {} : {method}),payload:body,fingerprint,key:uuid(),createdAt:new Date().toISOString()};write(key,record);}
  return send(client,key,record,config);
}
export function pendingStockOperation(scope) { try{return read(identity(scope));}catch(err){return {error:err.message,scope};} }
export async function retryPendingStockOperation(client,scope) {
  const key=identity(scope),record=read(key);
  if(!record)throw error("STOCK_RETRY_NOT_FOUND", "No pending request in this tab.");
  return send(client,key,record,{withCredentials:true,headers:{"Content-Type":"application/json"}});
}

export const idempotentPost = (client, url, payload, config) => idempotentWrite(client, "post", url, payload, config);
export const idempotentPut = (client, url, payload, config) => idempotentWrite(client, "put", url, payload, config);
export const idempotentPatch = (client, url, payload, config) => idempotentWrite(client, "patch", url, payload, config);

